import { useNavigate, Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, Bookmark, Clock, Eye } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { VerificationTick, RenderWithMentions } from "@/components/shared";
import { MediaDisplay } from "@/components/shared/MediaDisplay";

export const TopicCard = ({ topic, onLike, onFollow, onShare }) => {
  const navigate = useNavigate();
  
  const getTypeColor = (type) => {
    switch (type) {
      case "discussion": return "bg-blue-100 text-blue-700";
      case "mock": return "bg-amber-100 text-amber-700";
      case "donation": return "bg-emerald-100 text-emerald-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "discussion": return "Discussion";
      case "mock": return "Validation";
      case "donation": return "Donation";
      default: return type;
    }
  };

  return (
    <Card 
      className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" 
      data-topic-id={topic.id}
      onClick={() => { 
        sessionStorage.setItem('feed_return_topic', topic.id);
        navigate(`/topic/${topic.id}`); 
      }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar>
              <AvatarImage src={topic.author?.picture} />
              <AvatarFallback className="bg-jeekota-primary text-white">
                {topic.author?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-medium flex items-center min-w-0">
                <span className="truncate">{topic.author?.name || "Anonymous"}</span>
                <VerificationTick type={topic.author?.verification_tick} />
              </p>
              <p className="text-xs text-jeekota-text-secondary">
                {topic.author?.username && (
                  <Link
                    to={`/user/${topic.author.username}`}
                    className="text-jeekota-primary hover:underline mr-1.5"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`author-link-${topic.id}`}
                  >@{topic.author.username}</Link>
                )}
                <span>{topic.created_at_relative || "Just now"}</span>
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <h3 className="font-semibold text-lg mb-2">{topic.title}</h3>
        <p className="text-jeekota-text-secondary line-clamp-3"><RenderWithMentions text={topic.description} /></p>
        
        {topic.media && topic.media.length > 0 && (
          <div className="mt-3 rounded-xl overflow-hidden">
            <MediaDisplay media={topic.media} compact />
          </div>
        )}
        
        {/* Category (left) + Type (right) row */}
        <div className="flex items-center justify-between mt-3">
          <div>
            {topic.category && (
              <Badge className="bg-jeekota-primary/10 text-jeekota-primary" variant="secondary">
                {topic.category}
              </Badge>
            )}
            {topic.campaign_status === "successful" && (
              <Badge className="bg-emerald-100 text-emerald-700 ml-1.5" variant="secondary">
                Successful
              </Badge>
            )}
          </div>
          <Badge className={getTypeColor(topic.type)}>{getTypeLabel(topic.type)}</Badge>
        </div>

        {(topic.type === "mock" || topic.type === "donation") && topic.target_amount && (
          <div className="mt-3">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">{topic.currency?.symbol || "₦"}{(topic.raised_amount || 0).toLocaleString()}</span>
              <span className="text-jeekota-text-secondary">of {topic.currency?.symbol || "₦"}{topic.target_amount.toLocaleString()}</span>
            </div>
            <Progress value={Math.min((topic.raised_amount || 0) / topic.target_amount * 100, 100)} className="h-2" />
            <p className="text-xs text-jeekota-text-secondary mt-1">{Math.round((topic.raised_amount || 0) / topic.target_amount * 100)}% funded</p>
            {(topic.type === "donation" || topic.type === "mock") && topic.end_date && (
              <div className="flex items-center gap-1 mt-2 text-sm text-jeekota-text-secondary">
                <Clock size={14} />
                <span>{topic.days_remaining || 0} days remaining</span>
              </div>
            )}
          </div>
        )}
        
      </CardContent>
      
      <CardFooter className="pt-2 border-t border-jeekota-border">
        <div className="flex items-center justify-between w-full" onClick={(e) => e.stopPropagation()}>
          <Button 
            variant="ghost" 
            size="sm" 
            className={topic.is_liked ? "text-red-500" : "text-jeekota-text-secondary"}
            onClick={() => onLike?.(topic.id)}
            data-testid={`like-btn-${topic.id}`}
          >
            <Heart size={18} fill={topic.is_liked ? "currentColor" : "none"} />
            <span className="ml-1">{topic.likes_count || 0}</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-jeekota-text-secondary"
            onClick={() => navigate(`/topic/${topic.id}`)}
            data-testid={`comment-btn-${topic.id}`}
          >
            <MessageCircle size={18} />
            <span className="ml-1">{topic.comments_count || 0}</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-jeekota-text-secondary"
            onClick={() => onShare?.(topic)}
            data-testid={`share-btn-${topic.id}`}
          >
            <Share2 size={18} />
          </Button>
          <span className="flex items-center gap-1 text-xs text-jeekota-text-secondary px-1">
            <Eye size={14} /> {topic.views_count || 0}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            className={topic.is_following ? "text-jeekota-primary" : "text-jeekota-text-secondary"}
            onClick={() => onFollow?.(topic.id)}
            data-testid={`follow-btn-${topic.id}`}
            title={topic.is_following ? "Following - you'll get updates" : "Follow this topic"}
          >
            <Bookmark size={18} fill={topic.is_following ? "currentColor" : "none"} />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
